import React, { useState } from "react";
import { Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";

const PLATFORM_CHAR_LIMITS = {
    linkedin: 3000, twitter: 280, reddit: null, instagram: 2200,
};

export default function PostPreview({ generation, platform = "linkedin", draft = null }) {
    const [copied, setCopied] = useState(false);

    if (!generation) return null;

    const src = draft || generation;
    const { hook, body, cta, hashtags } = src;
    const response = generation.response;
    const finalText = response || [hook, body, cta, hashtags].filter(Boolean).join("\n\n");
    const charLimit = PLATFORM_CHAR_LIMITS[platform];

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(finalText);
            setCopied(true);
            toast.success("Copied — ready to paste");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Unable to copy");
        }
    };

    const download = () => {
        const blob = new Blob([finalText], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hilakshmi_${platform}_post.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Downloaded");
    };

    return (
        <div className="lk-border lk-surface rounded-xl p-6 md:p-8 relative overflow-hidden">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <div className="lk-label">Generated Post</div>
                    <div className="font-heading text-3xl mt-1 capitalize">{platform} Draft</div>
                </div>
                <div className="flex gap-2">
                    <button onClick={copy} className="lk-border rounded-full px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--lk-surface-2)]" data-testid="copy-post-btn">
                        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                        {copied ? "Copied" : "Copy"}
                    </button>
                    <button onClick={download} className="lk-border rounded-full px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--lk-surface-2)]" data-testid="download-post-btn">
                        <Download className="w-4 h-4" /> Download
                    </button>
                </div>
            </div>

            {charLimit && finalText.length > 0 && (
                <div className={`text-xs mb-4 font-mono ${finalText.length > charLimit ? "text-red-400" : "text-[var(--lk-muted)]"}`}>
                    {finalText.length} / {charLimit} chars {finalText.length > charLimit ? "⚠ over limit" : ""}
                </div>
            )}

            <div className="space-y-5">
                {hook && (
                    <div>
                        <div className="lk-label mb-1">Hook</div>
                        <div className="text-lg font-heading text-[var(--lk-primary)] leading-snug" data-testid="post-hook">{hook}</div>
                    </div>
                )}
                {body && (
                    <div>
                        <div className="lk-label mb-1">Body</div>
                        <div className="whitespace-pre-wrap leading-relaxed" data-testid="post-body">{body}</div>
                    </div>
                )}
                {cta && (
                    <div>
                        <div className="lk-label mb-1">Call to action</div>
                        <div className="italic" data-testid="post-cta">{cta}</div>
                    </div>
                )}
                {hashtags && (
                    <div>
                        <div className="lk-label mb-1">Hashtags</div>
                        <div className="font-mono text-sm text-[var(--lk-primary)]" data-testid="post-hashtags">{hashtags}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
